/** Workspace shell — тёмный chrome topbar по handoff-у RITM-FLOW.
 *
 *  Правила:
 *  - Хедер использует ИСКЛЮЧИТЕЛЬНО --chrome* токены (chrome, chrome-2,
 *    chrome-border, chrome-ink, chrome-ink-muted, chrome-active, chrome-hover).
 *  - Никаких --bg / --surface / --ink в хедере.
 *  - Статусные цвета (--success, --primary, --danger) одинаковы в обеих темах
 *    и допускаются на chrome для пиктограмм/индикаторов.
 */
import { type ParentComponent, For, Show } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { Activity, Calendar, Layers, Settings } from "lucide-solid";
import { useAuth } from "~/lib/auth";
import { useWorkspace } from "~/lib/workspace";
import { LocationDropdown } from "~/components/layout/LocationDropdown";
import { NotificationBell } from "~/components/layout/NotificationBell";
import { ProfileMenu } from "~/components/layout/ProfileMenu";
import { Sidebar } from "~/components/layout/Sidebar";
import { useI18n } from "~/i18n";
import type { Component } from "solid-js";

interface TabDef {
  href: string;
  titleKey: string;
  Icon: Component<{ size?: number; class?: string; "stroke-width"?: number }>;
  access: "monitoring" | "planning" | "configuration" | "system";
}

const TABS: TabDef[] = [
  { href: "/monitoring", titleKey: "shell.tabs.monitoring", Icon: Activity, access: "monitoring" },
  { href: "/planning", titleKey: "shell.tabs.planning", Icon: Calendar, access: "planning" },
  { href: "/configuration", titleKey: "shell.tabs.configuration", Icon: Layers, access: "configuration" },
  { href: "/system", titleKey: "shell.tabs.system", Icon: Settings, access: "system" },
];

export const WorkspaceShell: ParentComponent = (props) => {
  const { meQuery } = useAuth();
  const { activeLocation, availableLocations, setActiveLocationId } = useWorkspace();
  const location = useLocation();
  const { t } = useI18n();

  return (
    <div class="flex h-screen flex-col bg-bg">
      <header class="flex h-[var(--topbar-h)] flex-shrink-0 items-center gap-4 border-b border-chrome-border bg-chrome pr-4 text-chrome-ink">
        {/* Логотип */}
        <A
          href="/"
          class="flex w-[var(--nav-w)] flex-shrink-0 items-center px-[18px] no-underline"
          aria-label="RITM-FLOW"
        >
          <img src="/ritmflow.svg" alt="RITM-FLOW" class="h-8 w-auto" />
        </A>

        {/* Глобальный поиск временно скрыт. Спейсер забирает свободную ширину,
            чтобы вкладки оставались справа. */}
        <div class="flex-1" />

        {/* Section tabs */}
        <nav class="flex gap-1">
          <For each={TABS}>
            {(tab) => {
              const isActive = () => location.pathname.startsWith(tab.href);
              const access = () => meQuery.data?.access[tab.access] ?? false;
              return (
                <A
                  href={tab.href}
                  class="group relative flex flex-col items-center gap-1 rounded-md px-3.5 py-1.5 no-underline transition-colors"
                  classList={{
                    "bg-chrome-active text-chrome-ink": isActive(),
                    "text-chrome-ink-muted hover:bg-chrome-hover hover:text-chrome-ink":
                      !isActive() && access(),
                    "opacity-40 pointer-events-none": !access(),
                  }}
                >
                  <tab.Icon size={18} stroke-width={1.6} />
                  <span class="text-xs font-semibold">{t(tab.titleKey)}</span>
                  <Show when={isActive()}>
                    <span class="absolute -bottom-px left-[20%] right-[20%] h-0.5 rounded bg-primary" />
                  </Show>
                </A>
              );
            }}
          </For>
        </nav>

        <div class="flex-1" />

        {/* Локация */}
        <Show
          when={meQuery.data?.membership}
          fallback={<span class="text-xs text-chrome-ink-muted">{t("shell.noLocations")}</span>}
        >
          <LocationDropdown
            active={activeLocation()}
            available={availableLocations()}
            onSelect={setActiveLocationId}
          />
        </Show>

        {/* Уведомления */}
        <NotificationBell />

        {/* Profile menu */}
        <ProfileMenu />
      </header>

      <div class="flex flex-1 overflow-hidden">
        <Sidebar />
        <main class="flex-1 overflow-auto">{props.children}</main>
      </div>
    </div>
  );
};
